'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import Image from 'next/image'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowUpRightIcon,
  CalendarIcon,
  EditIcon,
  EyeIcon,
  FileIcon,
  GroupIcon,
  ImageIcon,
  MoreHorizontalIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  RefreshCwIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DeleteConfirmationDialog,
  VehicleGroupsFilters,
  LoadingState,
  SortableHeader,
  TableSortActions
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import {
  formatDateTime,
  getCountryCodeFromLocale,
  getLocalizedValue,
  getResizedPictureUrl,
  textLimit
} from '@/lib/utils'
import { useVehicleGroups, useDeleteVehicleGroup, useBulkDeleteVehicleGroups, useSortVehicleGroup } from '@/hooks'
import { VehicleGroupsFilterParams } from '@/types/vehicle-group'
import { useVehicleGroupsStore, useMetaStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { BulkDeleteResponse } from '@/types/general'
import { SortEnum, SortOrderEnum } from '@/enums'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteVehicleGroupId, setDeleteVehicleGroupId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  // Blog Post selection hooks from Zustand store
  const { mutate: sortVehicleGroup, isPending: isSorting } = useSortVehicleGroup()
  const selectedVehicleGroupIds = useVehicleGroupsStore(state => state.selectedVehicleGroupIds)
  const toggleVehicleGroupSelection = useVehicleGroupsStore(state => state.toggleVehicleGroupSelection)
  const selectAllVehicleGroups = useVehicleGroupsStore(state => state.selectAllVehicleGroups)
  const deselectAllVehicleGroups = useVehicleGroupsStore(state => state.deselectAllVehicleGroups)
  const isVehicleGroupSelected = useVehicleGroupsStore(state => state.isVehicleGroupSelected)

  const { metaState } = useMetaStore()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'VEHICLE_GROUPS', 'CREATE')
  const canShow = hasPermission(currentUser, 'VEHICLE_GROUPS', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'VEHICLE_GROUPS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'VEHICLE_GROUPS', 'DELETE')
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const { mutate: deleteVehicleGroupMutate, isPending: isDeleting } = useDeleteVehicleGroup()
  const { mutate: bulkDeleteVehicleGroupsMutate, isPending: isBulkDeleting } = useBulkDeleteVehicleGroups()

  const filters = useMemo<VehicleGroupsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'order',
      q: params.get('q') || '',
      isBlocked: params.get('isBlocked') === 'true' ? true : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: vehicleGroupsData, isLoading, error, refetch, isFetching } = useVehicleGroups(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<VehicleGroupsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<VehicleGroupsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/vehicle-groups')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (id: string) => {
    router.push(`/products/vehicle-groups/${id}`)
  }

  const handleEdit = (id: string) => {
    router.push(`/products/vehicle-groups/${id}/edit`)
  }

  const handleDelete = (id: string) => {
    setDeleteVehicleGroupId(id)
  }

  const handleSortOrder = (id: string, type: SortOrderEnum) => {
    sortVehicleGroup(
      { id, type },
      {
        onSuccess: async updatedVehicleGroup => {
          queryClient.setQueryData(['vehicle-group', id], updatedVehicleGroup)
          await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] })
          toast.success(t('sorted_successfully'))
        },
        onError: error => {
          console.error('Error sorting vehicleGroup:', error)
          toast.error(t('failed_to_sort'))
        }
      }
    )
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(id)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('vehicle_group_details')}
          </ItemComponent>
        )}

        {canUpdate && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleEdit(id)
            }}>
            <EditIcon className="h-4 w-4" />
            {t('edit_vehicle_group')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(id)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const confirmDeleteVehicleGroup = () => {
    if (deleteVehicleGroupId) {
      deleteVehicleGroupMutate(deleteVehicleGroupId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] })
          toast.success(t('deleted_successfully'))
          setDeleteVehicleGroupId(null)
        },
        onError: error => {
          console.error('Error deleting vehicleGroup:', error)
          toast.error(t('error_deleting'))
          setDeleteVehicleGroupId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedVehicleGroupIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteVehicleGroups = () => {
    if (selectedVehicleGroupIds.length > 0) {
      bulkDeleteVehicleGroupsMutate(selectedVehicleGroupIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (vehicleGroupsData && vehicleGroupsData.items.length > 0) {
      if (selectedVehicleGroupIds.length === vehicleGroupsData.items.length) {
        // If all vehicleGroups are selected, deselect all
        deselectAllVehicleGroups()
      } else {
        // Otherwise, select all vehicleGroups
        selectAllVehicleGroups(vehicleGroupsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GroupIcon className="h-5 w-5" />
                {t('vehicle_groups')}
              </CardTitle>
              <CardDescription>{t('vehicle_groups_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/products/vehicle-groups/create">
                    <SquarePlusIcon />
                    <span className="hidden items-center gap-2 lg:flex">{t('create_vehicle_group')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedVehicleGroupIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedVehicleGroupIds.length})</span>
                  </div>
                </Button>
              )}
              <VehicleGroupsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {vehicleGroupsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  vehicleGroupsData &&
                                  vehicleGroupsData.items.length > 0 &&
                                  selectedVehicleGroupIds.length === vehicleGroupsData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-12 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            {t('picture')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy={`translations.${locale}.title`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('title')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="order"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <ArrowUpRightIcon className="h-4 w-4" />
                            {t('sort_order')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vehicleGroupsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        vehicleGroupsData.items.map((vehicleGroup, index) => (
                          <ContextMenu key={vehicleGroup.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(vehicleGroup.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={isVehicleGroupSelected(vehicleGroup.id)}
                                        onCheckedChange={() => toggleVehicleGroupSelection(vehicleGroup.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="flex min-h-20 items-center justify-center py-4">
                                  {vehicleGroup.picture ? (
                                    <Image
                                      src={getResizedPictureUrl(vehicleGroup.picture.path, 90, 90)}
                                      alt={vehicleGroup.picture.name || ''}
                                      width={90}
                                      height={90}
                                      className="mx-auto max-h-22.5 w-auto border object-contain"
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <FileIcon className="text-muted-foreground h-10 w-10" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>
                                      {textLimit(getLocalizedValue(vehicleGroup, 'title', locale, defaultLocale), 120)}
                                    </span>
                                    <span className="text-muted-foreground text-xs">ID: {vehicleGroup.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="flex flex-row items-center justify-center gap-2">
                                    <span className="text-sm">{vehicleGroup.order}</span>
                                    {canUpdate && (
                                      <TableSortActions
                                        onSortUp={() => handleSortOrder(vehicleGroup.id, SortOrderEnum.UP)}
                                        onSortDown={() => handleSortOrder(vehicleGroup.id, SortOrderEnum.DOWN)}
                                        disabled={isSorting}
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(vehicleGroup.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(vehicleGroup.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(vehicleGroup.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(vehicleGroup.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={vehicleGroupsData.page}
                  totalPages={vehicleGroupsData.pages}
                  totalItems={vehicleGroupsData.total}
                  pageSize={vehicleGroupsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteVehicleGroupId}
          onCancel={() => setDeleteVehicleGroupId(null)}
          onConfirm={confirmDeleteVehicleGroup}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteVehicleGroups}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}
    </>
  )
}

export default List
